import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  MessageCircle,
  CheckCircle2,
  XCircle,
  Activity,
  ArrowDownToLine,
  ArrowUpFromLine,
  Percent,
  X,
} from 'lucide-react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const emptyForm = {
  id: null,
  name: '',
  keywords: '',
  reply_text: '',
  active: true,
  priority: 100,
  buttons: [],
};

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('ca-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const AdminWhatsappAutoreplies = () => {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchItems();
    fetchStats();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/whatsapp-autoreplies`);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      toast.error('Error carregant les respostes automàtiques');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await authFetch(
        `${API_URL}/api/admin/whatsapp-stats?days=14`
      );
      if (!res.ok) throw new Error();
      setStats(await res.json());
    } catch {
      // silent — stats are non-critical
    }
  };

  const openCreate = () => {
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setForm({
      id: item.id,
      name: item.name || '',
      keywords: (item.keywords || []).join(', '),
      reply_text: item.reply_text || '',
      active: !!item.active,
      priority: item.priority ?? 100,
      buttons: Array.isArray(item.buttons) ? item.buttons : [],
    });
    setDialogOpen(true);
  };

  const addButton = () => {
    if (form.buttons.length >= 3) {
      toast.error('Màxim 3 botons per missatge (límit de WhatsApp)');
      return;
    }
    setForm({
      ...form,
      buttons: [
        ...form.buttons,
        { id: `btn_${form.buttons.length + 1}`, title: '' },
      ],
    });
  };

  const updateButton = (idx, field, value) => {
    const next = [...form.buttons];
    next[idx] = { ...next[idx], [field]: value };
    setForm({ ...form, buttons: next });
  };

  const removeButton = (idx) => {
    setForm({ ...form, buttons: form.buttons.filter((_, i) => i !== idx) });
  };

  const handleSave = async () => {
    const keywords = form.keywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);

    if (!form.name.trim()) {
      toast.error('El nom és obligatori');
      return;
    }
    if (!form.reply_text.trim()) {
      toast.error('El text de resposta és obligatori');
      return;
    }
    if (keywords.length === 0) {
      toast.error('Cal almenys una paraula clau');
      return;
    }

    const payload = {
      name: form.name.trim(),
      keywords,
      reply_text: form.reply_text,
      active: form.active,
      priority: Number(form.priority) || 0,
      buttons: form.buttons
        .filter((b) => b && b.title && b.title.trim())
        .map((b) => ({
          id: (b.id || '').trim() || `btn_${Math.random().toString(36).slice(2, 8)}`,
          title: b.title.trim().slice(0, 20),
        })),
    };

    setSaving(true);
    try {
      const url = form.id
        ? `${API_URL}/api/admin/whatsapp-autoreplies/${form.id}`
        : `${API_URL}/api/admin/whatsapp-autoreplies`;
      const method = form.id ? 'PUT' : 'POST';
      const res = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      toast.success(form.id ? 'Resposta actualitzada' : 'Resposta creada');
      setDialogOpen(false);
      fetchItems();
      fetchStats();
    } catch (e) {
      toast.error(e.message || 'Error guardant');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Eliminar "${item.name}"?`)) return;
    try {
      const res = await authFetch(
        `${API_URL}/api/admin/whatsapp-autoreplies/${item.id}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error();
      toast.success('Resposta eliminada');
      fetchItems();
    } catch {
      toast.error("Error eliminant");
    }
  };

  const toggleActive = async (item) => {
    try {
      const res = await authFetch(
        `${API_URL}/api/admin/whatsapp-autoreplies/${item.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ active: !item.active }),
        }
      );
      if (!res.ok) throw new Error();
      fetchItems();
    } catch {
      toast.error("Error actualitzant l'estat");
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl mx-auto" data-testid="whatsapp-autoreplies-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <MessageCircle className="w-8 h-8 text-emerald-600" />
              Bot WhatsApp · Respostes automàtiques
            </h1>
            <p className="text-gray-600 mt-1">
              Configura les paraules clau i els missatges que Kaddy enviarà automàticament.
            </p>
          </div>
          <Button
            onClick={openCreate}
            data-testid="create-autoreply-btn"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nova resposta
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center text-gray-500">
            <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Cap resposta automàtica configurada.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <Card
                key={item.id}
                className="p-5"
                data-testid={`autoreply-card-${item.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-2">
                      <h3 className="text-lg font-semibold">{item.name}</h3>
                      {item.active ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Actiu
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inactiu
                        </Badge>
                      )}
                      <Badge variant="outline">Prioritat: {item.priority}</Badge>
                      <Badge variant="outline" className="text-emerald-700">
                        <Activity className="w-3 h-3 mr-1" />
                        {item.trigger_count || 0} disparades
                      </Badge>
                    </div>

                    <div className="flex flex-wrap gap-1 mb-3">
                      {(item.keywords || []).map((kw) => (
                        <span
                          key={kw}
                          className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-100"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>

                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans bg-gray-50 p-3 rounded border border-gray-100 max-h-40 overflow-auto">
                      {item.reply_text}
                    </pre>

                    <p className="text-xs text-gray-400 mt-2">
                      Última disparada: {formatDate(item.last_triggered_at)} · Actualitzat: {formatDate(item.updated_at)}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 shrink-0">
                    <Switch
                      checked={item.active}
                      onCheckedChange={() => toggleActive(item)}
                      data-testid={`toggle-${item.id}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEdit(item)}
                      data-testid={`edit-${item.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(item)}
                      data-testid={`delete-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {form.id ? 'Editar resposta' : 'Nova resposta automàtica'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label htmlFor="ar-name">Nom (intern)</Label>
                <Input
                  id="ar-name"
                  data-testid="autoreply-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Programa SIG"
                />
              </div>

              <div>
                <Label htmlFor="ar-keywords">
                  Paraules clau (separades per coma)
                </Label>
                <Input
                  id="ar-keywords"
                  data-testid="autoreply-keywords-input"
                  value={form.keywords}
                  onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                  placeholder="sig, curs, classes, escola, academia"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Es comparen com a paraula sencera, sense distinció de majúscules.
                </p>
              </div>

              <div>
                <Label htmlFor="ar-reply">Text de resposta</Label>
                <Textarea
                  id="ar-reply"
                  data-testid="autoreply-reply-input"
                  rows={10}
                  value={form.reply_text}
                  onChange={(e) =>
                    setForm({ ...form, reply_text: e.target.value })
                  }
                  placeholder="Hola! Sóc Kaddy..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Format WhatsApp: *negreta*, _cursiva_, ~ratllat~, ```codi```. Emojis suportats.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ar-priority">Prioritat (menor = abans)</Label>
                  <Input
                    id="ar-priority"
                    type="number"
                    data-testid="autoreply-priority-input"
                    value={form.priority}
                    onChange={(e) =>
                      setForm({ ...form, priority: e.target.value })
                    }
                  />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch
                    checked={form.active}
                    onCheckedChange={(v) => setForm({ ...form, active: v })}
                    data-testid="autoreply-active-switch"
                  />
                  <Label>Actiu</Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
              >
                Cancel·lar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                data-testid="autoreply-save-btn"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {form.id ? 'Actualitzar' : 'Crear'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminWhatsappAutoreplies;
